import { useCallback, useEffect, useState } from 'react';
import { haApi } from '../../lib/api';
import { useAppStore } from '../../stores/appStore';

interface HaEntity {
  entity_id: string;
  state: string;
  friendly_name?: string;
  attributes?: Record<string, unknown>;
}

export function SmartHomeWidget() {
  const user = useAppStore((s) => s.user);
  const [entities, setEntities] = useState<HaEntity[]>([]);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    haApi
      .status()
      .then((res) => {
        setConfigured(Boolean(res.configured));
        if (res.configured && res.online) {
          setEntities((res.entities as HaEntity[]) || []);
          setMessage('');
        } else if (res.configured) {
          setMessage(String(res.error || 'Home Assistant offline'));
        } else {
          setMessage(String(res.message || 'Not configured'));
        }
      })
      .catch((e) => setMessage(e instanceof Error ? e.message : 'Error'));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  async function toggle(entityId: string) {
    if (!user) return;
    setBusy(entityId);
    try {
      await haApi.toggle(entityId);
      // Optimistic flip
      setEntities((list) =>
        list.map((e) =>
          e.entity_id === entityId
            ? {
                ...e,
                state: e.state === 'on' ? 'off' : e.state === 'off' ? 'on' : e.state,
              }
            : e
        )
      );
      setTimeout(load, 500);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Toggle failed');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex h-full flex-col">
      <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
        Smart home
      </h3>
      {configured === false && (
        <p className="text-sm text-slate-500">
          Connect Home Assistant in <code className="text-violet-300">.env</code>{' '}
          (HA_URL, HA_TOKEN, HA_QUICK_ENTITIES). HA bridges Alexa reliably.
        </p>
      )}
      {configured && message && (
        <p className="mb-2 text-xs text-amber-400">{message}</p>
      )}
      <div className="grid grid-cols-2 gap-2 overflow-auto sm:grid-cols-3">
        {entities.map((e) => {
          const on = e.state === 'on' || e.state === 'home' || e.state === 'open';
          const name =
            e.friendly_name ||
            String(e.attributes?.friendly_name || e.entity_id.split('.')[1]);
          return (
            <button
              key={e.entity_id}
              type="button"
              disabled={!user || busy === e.entity_id}
              onClick={() => void toggle(e.entity_id)}
              className={`rounded-xl border px-2 py-3 text-left transition ${
                on
                  ? 'border-amber-400/50 bg-amber-500/20 text-amber-100'
                  : 'border-slate-700 bg-slate-800/50 text-slate-300'
              } disabled:opacity-50`}
            >
              <div className="text-lg">{on ? '💡' : '🌑'}</div>
              <div className="truncate text-xs font-semibold">{name}</div>
              <div className="text-[10px] uppercase opacity-70">{e.state}</div>
            </button>
          );
        })}
      </div>
      {configured && entities.length === 0 && !message && (
        <p className="text-sm text-slate-500">No quick entities configured</p>
      )}
    </div>
  );
}
